import type {Organization} from "crqlar-react-common/dist/models/organization/organization";
import type {GuestAppPreviewSession} from "crqlar-react-common/dist/models/guestApp/guestAppPreviewSession.model";

/**
 * The dashboard's half of the App preview handshake.
 *
 * The preview loads the organization's guest app on the web inside a frame, with `?preview=1` and
 * no token. The app says it is ready; only then does the dashboard send it the session, addressed
 * to the app's exact origin. The token therefore never appears in a URL, where history, hosting
 * logs and Referer headers would keep it.
 *
 * Kept pure and apart from the component so the rules that decide who gets a session are settled
 * by tests rather than by trying it in a browser.
 */

/**
 * Where this organization's guest app runs on the web.
 *
 * A tag on the organization rather than a check for one customer: the organization that has it
 * gets the App preview tab, and nothing in the code names Forsthofgut. Locally it points at Expo
 * on this machine, in production at the hosted build.
 */
export const APP_PREVIEW_URL_TAG = "app_preview_url";

export const PREVIEW_READY_MESSAGE = "crqlar-preview-ready";
export const PREVIEW_SESSION_MESSAGE = "crqlar-preview-session";

/** The configured address, if it is one a frame can load. Anything else hides the tab. */
export const appPreviewUrl = (organization?: Pick<Organization, "tags"> | null): string | undefined => {
    const raw = organization?.tags?.[APP_PREVIEW_URL_TAG];
    if (typeof raw !== "string") return undefined;

    const trimmed = raw.trim();
    try {
        const url = new URL(trimmed);
        return url.protocol === "http:" || url.protocol === "https:" ? trimmed : undefined;
    } catch {
        return undefined;
    }
};

/** The address the frame loads: the app, asked to wait for a session instead of showing a login. */
export const previewFrameSrc = (appUrl: string): string => {
    const url = new URL(appUrl);
    url.searchParams.set("preview", "1");
    return url.toString();
};

/** The only origin the session is ever sent to, and the only one a ready message is taken from. */
export const previewOrigin = (appUrl: string): string => new URL(appUrl).origin;

interface IncomingMessage {
    origin: string;
    source: unknown;
    data: unknown;
}

/**
 * Whether a message is our own frame saying it is ready for the session.
 *
 * All three have to hold. Any page can post a message to this window, including another tab of
 * the same app, and only the frame on this screen should receive a session for this guest.
 */
export const isPreviewReady = (message: IncomingMessage, expectedOrigin: string, frameWindow: unknown): boolean =>
    !!frameWindow
    && message.source === frameWindow
    && message.origin === expectedOrigin
    && (message.data as {type?: unknown} | null)?.type === PREVIEW_READY_MESSAGE;

export const previewSessionMessage = (session: GuestAppPreviewSession) => ({
    type: PREVIEW_SESSION_MESSAGE,
    claims: session,
});
